import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { User } from '@supabase/supabase-js';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToResources = () => {
    setIsResourcesOpen(false);
    const resourcesSection = document.getElementById('resources');
    if (resourcesSection) {
      resourcesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleConsultationClick = () => {
    setIsMobileMenuOpen(false);
    navigate('/consultation');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const handleMobileLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const getUserInitials = () => {
    if (!user) return '';
    const name = user.user_metadata?.full_name || user.email || '';
    return name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-sm py-4' : 'bg-white py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/d817fcc14e7e8fe425753ab5de5ab717.jpeg" 
                alt="Vazcular Logo" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-semibold text-gray-900">Vazcular</span>
            </Link>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-10">
              <Link
                to="/our-story"
                className="text-sm font-normal text-gray-700 hover:text-[#f04e58] transition-colors cursor-pointer whitespace-nowrap"
              >
                Our Story
              </Link>
              <Link
                to="/our-approach"
                className="text-sm font-normal text-gray-700 hover:text-[#f04e58] transition-colors cursor-pointer whitespace-nowrap"
              >
                Our Approach
              </Link>
              <Link
                to="/topics"
                className="text-sm font-normal text-gray-700 hover:text-[#f04e58] transition-colors cursor-pointer whitespace-nowrap"
              >
                What is FH?
              </Link>
              <Link
                to="/consultation"
                className="text-sm font-normal text-gray-700 hover:text-[#f04e58] transition-colors cursor-pointer whitespace-nowrap"
              >
                Topics to Consult
              </Link>
              <button
                onClick={handleConsultationClick}
                className="inline-flex items-center px-6 py-2 bg-[#f04e58] text-white text-sm font-medium rounded-full hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap"
              >
                Book a Free Consultation
              </button>

              {/* Auth Button / User Menu */}
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="w-10 h-10 flex items-center justify-center bg-[#f04e58] text-white rounded-full hover:bg-[#d93d47] transition-all cursor-pointer font-medium text-sm"
                  >
                    {getUserInitials()}
                  </button>
                  {isUserMenuOpen && (
                    <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-lg py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">
                          {user.user_metadata?.full_name || 'User'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{user.email}</p>
                      </div>
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigate('/profile');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-[#fef5f5] hover:text-[#f04e58] transition-colors cursor-pointer flex items-center gap-2"
                      >
                        <i className="ri-settings-3-line"></i>
                        Profile Settings
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center gap-2"
                      >
                        <i className="ri-logout-box-line"></i>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/auth"
                  className="inline-flex items-center gap-2 px-5 py-2 border border-[#f04e58] text-[#f04e58] text-sm font-medium rounded-full hover:bg-[#fef5f5] transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-user-line"></i>
                  Sign In
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>

          {/* Mobile Menu Panel */}
          <div className="fixed top-[72px] left-0 right-0 bg-white z-40 md:hidden shadow-lg max-h-[calc(100vh-72px)] overflow-y-auto">
            <div className="px-6 py-6 space-y-1">
              {/* Navigation Links */}
              <Link
                to="/our-story"
                onClick={handleMobileLinkClick}
                className="block py-3 text-base font-normal text-gray-700 hover:text-[#f04e58] hover:bg-[#fef5f5] rounded-lg px-4 transition-colors cursor-pointer"
              >
                Our Story
              </Link>
              <Link
                to="/our-approach"
                onClick={handleMobileLinkClick}
                className="block py-3 text-base font-normal text-gray-700 hover:text-[#f04e58] hover:bg-[#fef5f5] rounded-lg px-4 transition-colors cursor-pointer"
              >
                Our Approach
              </Link>
              <Link
                to="/topics"
                onClick={handleMobileLinkClick}
                className="block py-3 text-base font-normal text-gray-700 hover:text-[#f04e58] hover:bg-[#fef5f5] rounded-lg px-4 transition-colors cursor-pointer"
              >
                What is FH?
              </Link>
              <Link
                to="/consultation"
                onClick={handleMobileLinkClick}
                className="block py-3 text-base font-normal text-gray-700 hover:text-[#f04e58] hover:bg-[#fef5f5] rounded-lg px-4 transition-colors cursor-pointer"
              >
                Topics to Consult
              </Link>

              {/* CTA Button */}
              <div className="pt-4 pb-4">
                <button
                  onClick={handleConsultationClick}
                  className="w-full inline-flex items-center justify-center px-6 py-3 bg-[#f04e58] text-white text-base font-medium rounded-full hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap"
                >
                  Book a Free Consultation
                </button>
              </div>

              {/* Auth Section */}
              {user ? (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-lg mb-2">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#f04e58] text-white rounded-full font-medium text-sm">
                      {getUserInitials()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.user_metadata?.full_name || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/profile');
                    }}
                    className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-[#fef5f5] hover:text-[#f04e58] rounded-lg transition-colors cursor-pointer flex items-center gap-3"
                  >
                    <i className="ri-settings-3-line text-lg"></i>
                    Profile Settings
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-3 text-base text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer flex items-center gap-3"
                  >
                    <i className="ri-logout-box-line text-lg"></i>
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <Link
                    to="/auth"
                    onClick={handleMobileLinkClick}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#f04e58] text-[#f04e58] text-base font-medium rounded-full hover:bg-[#fef5f5] transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-user-line"></i>
                    Sign In
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;